import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { AgendaSettings } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { useFeature } from "@/lib/use-feature";
import { useConfirm } from "@/lib/confirm";
import { useSwitchOrganization } from "@/lib/use-switch-organization";
import { useEmOutraOrganizacao } from "@/components/organization/painel/OrganizacaoEmFoco";
import { timeToMinutes } from "@/lib/agenda-time";
import { useAppointmentConflict } from "@/lib/appointment-conflict";
import { titleCasePtBR } from "@/lib/name-case";
import { aplicarSelecao, initialFormValues, textoDosServicos, type DialogInitial, type FormValues } from "./formulario-da-consulta";
import type { ServicoDoCatalogo } from "./BuscaDeServicos";
import type { OpcaoDeClinica } from "./CabecalhoDaConsulta";
import { useSalaDaConsulta } from "./use-sala-da-consulta";
import { useLembreteDeAnamnese } from "./use-lembrete-de-anamnese";

type Dentista = { id: string; name: string; assignedServiceIds?: string[] };
type OrgInicial = { id: string; name: string; isPersonal: boolean } | null;

/** Estado, dados e ações do diálogo de consulta. */
export function useFormularioDaConsulta(p: {
  open: boolean;
  mode: "create" | "edit";
  initial: DialogInitial;
  settings: AgendaSettings;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { open, mode, initial, settings } = p;
  const confirm = useConfirm();
  const utils = trpc.useUtils();
  const { user } = useAuth();
  const comLembrete = useFeature("agenda.whatsappReminder");
  const comCatalogo = useFeature("clinicServices");
  const { switchTo, isSwitching } = useSwitchOrganization();
  // Aberto pra outra clínica sem trocar a ativa: trocar daqui mudaria a organização de verdade.
  const emOutraOrganizacao = useEmOutraOrganizacao();
  const minhas = trpc.organization.listMine.useQuery(undefined, { enabled: open && mode === "create", staleTime: 60_000 });
  const dentistas = (trpc.organization.listDentists.useQuery(undefined, { staleTime: 60_000 }).data ?? []) as Dentista[];
  // Catálogo inteiro: a busca filtra localmente. Só carrega com a feature.
  const servicosQuery = trpc.service.list.useQuery({ status: "ACTIVE", take: 500, skip: 0 }, { enabled: comCatalogo, staleTime: 60_000 });
  const servicos = (servicosQuery.data?.services ?? []) as ServicoDoCatalogo[];

  const [values, setValues] = useState<FormValues>(() => initialFormValues(initial, settings));
  const [orgInicial, setOrgInicial] = useState<OrgInicial>(null);
  const update = (parcial: Partial<FormValues>) => setValues((v) => ({ ...v, ...parcial }));
  const org = user?.activeOrganization;

  useEffect(() => {
    if (!open) return;
    setValues(initialFormValues(initial, settings));
    setOrgInicial(mode === "create" && org ? { id: org.id, name: org.name, isPersonal: org.isPersonal } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial.id, initial.date, initial.startTime]);

  // Sem profissional escolhido: o usuário logado, se atende aqui; senão o primeiro da lista.
  useEffect(() => {
    if (!open || values.dentistId || dentistas.length === 0) return;
    const d = dentistas.find((x) => x.id === user?.id) ?? dentistas[0];
    update({ dentistId: d.id, dentistName: d.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dentistas, user?.id]);

  // Clínicas onde dá pra marcar: as da pessoa (menos as só de leitura), e a Pessoal se o diálogo
  // abriu nela. A ativa sempre entra, mesmo que o filtro a deixasse de fora.
  const opcoesDeClinica: OpcaoDeClinica[] = (minhas.data ?? [])
    .filter(
      (m) =>
        m.organization.id === org?.id ||
        (m.role !== "VIEWER" && !m.organization.isPersonal && m.organization.types.includes("DENTAL_OFFICE")) ||
        (orgInicial?.isPersonal && m.organization.id === orgInicial.id),
    )
    .map((m) => ({ id: m.organization.id, label: m.organization.isPersonal ? `${m.organization.name} (Pessoal)` : m.organization.name }));

  const trocarClinica = async (id: string) => {
    if (id === org?.id) return;
    try {
      await switchTo(id);
      // Paciente, profissional e serviços são da clínica anterior: força nova escolha.
      setValues((v) => ({ ...v, patient: { id: null, name: v.patient.name, phone: v.patient.phone }, dentistId: null, dentistName: "", serviceIds: [], salaId: null }));
    } catch (e) {
      toast.error((e as Error).message || "Erro ao trocar de clínica.");
    }
  };

  const voltarClinica = () => {
    if (orgInicial && org?.id !== orgInicial.id) switchTo(orgInicial.id).catch(() => {});
  };

  const sala = useSalaDaConsulta({ open, mode, values, update, settings, salaAtual: initial.sala, fixada: initial.salaId !== undefined });
  const conflito = useAppointmentConflict({
    enabled: open,
    date: values.date,
    startTime: values.startTime,
    endTime: values.endTime,
    dentistId: values.dentistId,
    salaId: values.salaId,
    editingId: mode === "edit" ? initial.id : undefined,
  });
  // Sala ocupada sempre barra; profissional ocupado só se a agenda estiver em "bloquear".
  const bloqueado = !!conflito.sala || (conflito.dentista && settings.conflictBehavior === "block");

  const fechar = () => {
    p.onSaved();
    p.onClose();
  };
  const lembrarAnamnese = useLembreteDeAnamnese();
  const criar = trpc.appointment.create.useMutation({
    onSuccess: (consulta) => {
      toast.success("Consulta agendada");
      void lembrarAnamnese(consulta.patientId, consulta.patientName);
      // Paciente digitado sem selecionar da lista (walk-in) pode ter sido cadastrado agora.
      utils.patient.list.invalidate();
      fechar();
    },
    onError: (e) => toast.error(e.message || "Erro ao criar"),
  });
  const atualizar = trpc.appointment.update.useMutation({
    onSuccess: () => {
      toast.success("Consulta atualizada");
      fechar();
    },
    onError: (e) => toast.error(e.message || "Erro ao atualizar"),
  });
  const remover = trpc.appointment.remove.useMutation({
    onSuccess: () => {
      toast.success("Consulta removida");
      fechar();
    },
    onError: (e) => toast.error(e.message || "Erro ao remover"),
  });

  const salvar = () => {
    const v = values;
    if (v.patient.name.trim().length < 2) return void toast.error("Nome do paciente obrigatório");
    if (timeToMinutes(v.startTime) >= timeToMinutes(v.endTime)) return void toast.error("Hora final deve ser depois da inicial");
    if (bloqueado) return void toast.error(conflito.sala ? `${conflito.sala.nome} já está ocupada nesse horário` : "Conflito de horário — não permitido");
    const payload = {
      patientId: v.patient.id ?? undefined,
      // Title case só ao gravar — durante a digitação brigaria com cursor/busca.
      patientName: v.patient.id ? v.patient.name.trim() : titleCasePtBR(v.patient.name.trim()),
      patientPhone: v.patient.phone.replace(/\D/g, "") || undefined,
      dentistId: v.dentistId ?? undefined,
      dentistName: v.dentistName.trim() || undefined,
      serviceIds: v.serviceIds,
      procedure: v.procedure.trim(),
      date: new Date(`${v.date}T00:00:00`),
      startTime: v.startTime,
      endTime: v.endTime,
      status: v.status,
      notes: v.notes.trim() || undefined,
      // Sem a feature o interruptor some — nunca manda true escondido.
      sendReminder: comLembrete ? v.sendReminder : false,
      color: v.procedureColor ?? undefined,
      // Quem abriu o diálogo sem saber a sala (undefined) não apaga a que a consulta tem.
      salaId: v.salaId ?? (initial.salaId === undefined ? undefined : null),
    };
    if (mode === "create") criar.mutate(payload);
    else if (initial.id) atualizar.mutate({ id: initial.id, data: payload });
  };

  const excluir = async () => {
    if (!initial.id) return;
    const ok = await confirm({
      title: `Excluir consulta de ${initial.patientName}?`,
      description: "Essa ação é permanente. Se só quer cancelar, mude o status para Cancelado.",
      confirmText: "Excluir",
      cancelText: "Cancelar",
      destructive: true,
    });
    if (ok) remover.mutate({ id: initial.id });
  };

  return {
    values,
    update,
    comLembrete,
    comCatalogo,
    dentistas,
    servicos,
    recarregarServicos: () => servicosQuery.refetch(),
    atribuidos: dentistas.find((d) => d.id === values.dentistId)?.assignedServiceIds ?? [],
    textoDosServicos: textoDosServicos(values.serviceIds, servicos),
    alternarServico: (id: string) =>
      update(
        aplicarSelecao(values, values.serviceIds.includes(id) ? values.serviceIds.filter((x) => x !== id) : [...values.serviceIds, id], servicos, settings.endHour),
      ),
    // A duração fica como está: mexer no fim do card sem o usuário pedir surpreende mais do que ajuda.
    limparServicos: () => update({ serviceIds: [], procedure: "", procedureColor: null }),
    clinica: { nome: org?.name ?? "", id: org?.id ?? "", opcoes: mode === "create" && !emOutraOrganizacao ? opcoesDeClinica : [], trocando: isSwitching, trocar: trocarClinica },
    voltarClinica,
    sala,
    conflito,
    bloqueado,
    salvar,
    excluir,
    salvando: criar.isPending || atualizar.isPending,
    excluindo: remover.isPending,
  };
}
